// Table: the one way the interface prints a grid of numbers (encounter results, settlement rows).
// Columns are declared, not hand-rolled, so every table gets the same head styling, the same
// staggered row entrance (`rise`) and the same empty row. `num` right-aligns and sets the display
// face for a column of figures.
import type { ReactNode } from 'react';
import { stagger } from './Motion';

export interface Column<R> {
  key: string;
  head: ReactNode;
  /** Right-aligned, display face — for a column of figures. */
  num?: boolean;
  width?: string;
  cell: (row: R, index: number) => ReactNode;
}

export function Table<R>({ columns, rows, rowKey, rowTestId, empty, rise = true, label, className = '', ...rest }: {
  columns: ReadonlyArray<Column<R>>;
  rows: ReadonlyArray<R>;
  rowKey: (row: R, index: number) => string;
  rowTestId?: string;
  /** Shown in a full-width row when there are no rows. */
  empty?: ReactNode;
  rise?: boolean;
  label?: string;
  className?: string;
  'data-testid'?: string;
}) {
  return (
    <table className={`fk-table ${rise ? 'fk-table--rise' : ''} ${className}`} aria-label={label} {...rest}>
      <thead>
        <tr>{columns.map((c) => <th key={c.key} className={c.num ? 'fk-table__num' : ''} style={c.width ? { width: c.width } : undefined}>{c.head}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && empty !== undefined && <tr><td colSpan={columns.length} className="fk-table__empty">{empty}</td></tr>}
        {rows.map((r, i) => (
          <tr key={rowKey(r, i)} style={stagger(i)} data-testid={rowTestId}>
            {columns.map((c) => <td key={c.key} className={c.num ? 'fk-table__num' : ''}>{c.cell(r, i)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
